import {
  Alert,
  Badge,
  Button,
  Container,
  Flex,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { IconArrowLeft, IconCheck, IconDotsVertical, IconFilter, IconSearch } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../components/AuthProvider';
import { AdminTransaction, TokenService } from '../../services/token.service';
import { UserRole } from '../../types/auth.types';
import { PaymentStatus } from '../../types/payment.types';

interface UpdateStatusModalState {
  open: boolean;
  transaction: AdminTransaction | null;
  status: PaymentStatus | '';
}

const TransactionManagement: React.FC = () => {
  const [transactions, setTransactions] = useState<AdminTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  });
  
  // Filters
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [filtersVisible, setFiltersVisible] = useState(false);
  
  // Modal state
  const [updateStatusModal, setUpdateStatusModal] = useState<UpdateStatusModalState>({
    open: false,
    transaction: null,
    status: ''
  });
  
  const { user } = useAuthContext();
  
  // Kiểm tra quyền admin
  useEffect(() => {
    if (user?.role !== UserRole.ADMIN) {
      // Redirect to home if not admin
      window.location.href = '/';
    } else {
      // Fetch transactions if admin
      fetchTransactions();
    }
  }, [user]);
  
  const fetchTransactions = async (page: number = 1) => {
    try {
      setIsLoading(true);
      setError('');
      
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (searchText) filters.userId = searchText;
      
      const response = await TokenService.getAllTransactions(page, pagination.limit, filters);
      setTransactions(response.transactions);
      setPagination(response.pagination);
    } catch (err) {
      setError('Lỗi khi tải dữ liệu giao dịch');
      console.error('Error fetching transactions:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSearch = () => {
    fetchTransactions(1);
  };
  
  const clearFilters = () => {
    setSearchText('');
    setStatusFilter(null);
    fetchTransactions(1);
  };
  
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);
  };
  
  const getStatusColor = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.COMPLETED:
      case PaymentStatus.PAID:
        return 'green';
      case PaymentStatus.PENDING:
        return 'yellow';
      case PaymentStatus.FAILED:
      case PaymentStatus.CANCELLED:
      case PaymentStatus.EXPIRED:
        return 'red';
      default:
        return 'gray';
    }
  };
  
  const formatStatus = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.COMPLETED:
        return 'Hoàn thành';
      case PaymentStatus.PAID:
        return 'Đã thanh toán';
      case PaymentStatus.PENDING:
        return 'Đang chờ';
      case PaymentStatus.FAILED:
        return 'Thất bại';
      case PaymentStatus.CANCELLED:
        return 'Đã hủy';
      case PaymentStatus.EXPIRED:
        return 'Hết hạn';
      default:
        return status;
    }
  };
  
  const openUpdateStatusModal = (transaction: AdminTransaction) => {
    setUpdateStatusModal({
      open: true,
      transaction,
      status: transaction.status
    });
  };
  
  const closeUpdateStatusModal = () => {
    setUpdateStatusModal({ open: false, transaction: null, status: '' });
  };
  
  const updateStatusModalState = (status: PaymentStatus | '') => {
    setUpdateStatusModal({
      ...updateStatusModal,
      status: status as PaymentStatus
    });
  };
  
  const handleUpdateStatus = async () => {
    if (!updateStatusModal.transaction || !updateStatusModal.status) return;
    
    try {
      setIsLoading(true);
      setError('');
      
      const success = await TokenService.updatePaymentStatus(
        updateStatusModal.transaction.paymentId,
        updateStatusModal.status as PaymentStatus
      );
      
      if (success) {
        setSuccess(`Đã cập nhật trạng thái giao dịch ${updateStatusModal.transaction.paymentId} thành công`);
        setUpdateStatusModal({ open: false, transaction: null, status: '' });
        fetchTransactions(pagination.page);
      }
    } catch (err) {
      setError('Lỗi khi cập nhật trạng thái giao dịch');
      console.error('Error updating payment status:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const checkPaymentStatus = async (paymentId: string) => {
    try {
      setIsLoading(true);
      await TokenService.getPaymentDetails(paymentId);
      fetchTransactions(pagination.page);
    } catch (err) {
      setError('Lỗi khi kiểm tra trạng thái thanh toán');
      console.error('Error checking payment status:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const clearSuccess = () => setSuccess('');
  const clearError = () => setError('');
  
  return (
    <Container size="xl" py="xl">
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="md">
          <Group>
            <Button 
              component={Link} 
              to="/" 
              variant="subtle" 
              leftSection={<IconArrowLeft size={16} />}
            >
              Trở về trang chủ
            </Button>
            <Title order={3}>Quản lý giao dịch</Title>
          </Group>
          
          <Button 
            leftSection={<IconFilter size={16} />}
            onClick={() => setFiltersVisible(!filtersVisible)}
            variant="light"
          >
            Bộ lọc
          </Button>
        </Group>
        
        {filtersVisible && (
          <Paper p="md" withBorder mb="md">
            <Stack>
              <Title order={4}>Lọc giao dịch</Title>
              
              <Flex gap="md" wrap="wrap">
                <TextInput
                  label="Tìm theo ID người dùng"
                  placeholder="Nhập ID người dùng"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ flex: 1 }}
                />
                
                <Select
                  label="Trạng thái"
                  placeholder="Tất cả trạng thái"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ flex: 1 }}
                  data={[
                    { value: PaymentStatus.PENDING, label: 'Đang chờ' },
                    { value: PaymentStatus.PAID, label: 'Đã thanh toán' },
                    { value: PaymentStatus.COMPLETED, label: 'Hoàn thành' },
                    { value: PaymentStatus.FAILED, label: 'Thất bại' },
                    { value: PaymentStatus.CANCELLED, label: 'Đã hủy' },
                    { value: PaymentStatus.EXPIRED, label: 'Hết hạn' }
                  ]}
                  clearable
                />
              </Flex>              
              <Group justify="space-between">
                <Button variant="outline" onClick={clearFilters}>
                  Xóa bộ lọc
                </Button>
                
                <Button onClick={handleSearch} leftSection={<IconSearch size={16} />}>
                  Tìm kiếm
                </Button>
              </Group>
            </Stack>
          </Paper>
        )}
        
        {error && (
          <Alert color="red" title="Lỗi" withCloseButton onClose={clearError} mb="md">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert color="green" title="Thành công" withCloseButton onClose={clearSuccess} mb="md">
            {success}
          </Alert>
        )}
        
        {isLoading ? (
          <Flex justify="center" p="xl">
            <Loader size="lg" />
          </Flex>
        ) : transactions.length === 0 ? (
          <Alert color="gray">
            Không tìm thấy giao dịch nào phù hợp.
          </Alert>
        ) : (
          <>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID Giao dịch</Table.Th>
                  <Table.Th>Ngày</Table.Th>
                  <Table.Th>Người dùng</Table.Th>
                  <Table.Th>Gói</Table.Th>
                  <Table.Th>Số tiền</Table.Th>
                  <Table.Th>Trạng thái</Table.Th>
                  <Table.Th>Thao tác</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {transactions.map((transaction) => (
                  <Table.Tr key={transaction.id}>
                    <Table.Td>{transaction.paymentId}</Table.Td>
                    <Table.Td>{new Date(transaction.date).toLocaleDateString('vi-VN')}</Table.Td>
                    <Table.Td>
                      <div>
                        <Text size="sm" fw={600}>{transaction.userName}</Text>
                        <Text size="xs" c="dimmed">{transaction.userEmail}</Text>
                        <Text size="xs" c="dimmed">ID: {transaction.userId}</Text>
                      </div>
                    </Table.Td>
                    <Table.Td>{transaction.package?.name || 'N/A'}</Table.Td>
                    <Table.Td>{formatPrice(transaction.amount)}</Table.Td>
                    <Table.Td>
                      <Badge color={getStatusColor(transaction.status)}>
                        {formatStatus(transaction.status)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Menu width={200} position="bottom-end" withArrow>
                        <Menu.Target>
                          <Button variant="subtle" size="xs">
                            <IconDotsVertical size={16} />
                          </Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Label>Quản lý giao dịch</Menu.Label>
                          {transaction.status !== PaymentStatus.PAID && transaction.status !== PaymentStatus.COMPLETED && (
                            <Menu.Item 
                              leftSection={<IconCheck size={16} />}
                              onClick={() => openUpdateStatusModal(transaction)}
                            >
                              Cập nhật trạng thái
                            </Menu.Item>
                          )}
                          <Menu.Item
                            onClick={() => checkPaymentStatus(transaction.paymentId)}
                          >
                            Kiểm tra trạng thái
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            
            {pagination.pages > 1 && (
              <Group justify="center" mt="md">
                <Button 
                  variant="outline" 
                  disabled={pagination.page === 1}
                  onClick={() => fetchTransactions(pagination.page - 1)}
                >
                  Trang trước
                </Button>
                <Text>
                  Trang {pagination.page} / {pagination.pages}
                </Text>
                <Button 
                  variant="outline" 
                  disabled={pagination.page === pagination.pages}
                  onClick={() => fetchTransactions(pagination.page + 1)}
                >
                  Trang sau
                </Button>
              </Group>
            )}
          </>
        )}
      </Paper>
      
      <Modal
        opened={updateStatusModal.open}
        onClose={closeUpdateStatusModal}
        title="Cập nhật trạng thái giao dịch"
        size="md"
        centered
      >
        {updateStatusModal.transaction && (
          <Stack gap="md">
            <Text>Giao dịch: {updateStatusModal.transaction.paymentId}</Text>
            <Text>Người dùng: {updateStatusModal.transaction.userName} ({updateStatusModal.transaction.userEmail})</Text>
            <Text>Gói: {updateStatusModal.transaction.package?.name}</Text>
            <Text>Số tiền: {formatPrice(updateStatusModal.transaction.amount)}</Text>
            
            <Select
              label="Trạng thái mới"
              value={updateStatusModal.status}
              onChange={(value) => updateStatusModalState(value || '')}
              data={[
                { value: PaymentStatus.PAID, label: 'Đã thanh toán (PAID)' },
                { value: PaymentStatus.COMPLETED, label: 'Hoàn thành (COMPLETED)' },
                { value: PaymentStatus.FAILED, label: 'Thất bại (FAILED)' },
                { value: PaymentStatus.CANCELLED, label: 'Đã hủy (CANCELLED)' }
              ]}
            />
            
            <Alert color="yellow" title="Lưu ý">
              Nếu chọn "Đã thanh toán" hoặc "Hoàn thành", token sẽ tự động được cộng vào tài khoản người dùng.
            </Alert>
            
            <Group justify="space-between">
              <Button variant="outline" onClick={closeUpdateStatusModal}>
                Hủy
              </Button>
              <Button onClick={handleUpdateStatus} color="blue">
                Cập nhật
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Container>
  );
};

export default TransactionManagement; 